import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Icon,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core"
import {
  exportSummaryReportService,
  getLedgerSummaryService,
} from "lib/utils/api/ledgerSummary"
import React, { useCallback } from "react"
import { IInvoiceSummary, ILedgerSummaryResponse } from "types/response"
import { CustomFilters } from "./CustomFilters"
import ExportReportDialog from "./ExportReportDialog"
import {
  AccountTotal,
  COLOR_BORDER_LIGHT,
  COLOR_CHIP_BG,
  COLOR_ICON_EMPTY,
  COLOR_PRIMARY,
  COLOR_PRIMARY_DARK,
  COLOR_SURFACE_LIGHT,
  COLOR_TEXT_BORDER,
  COLOR_TEXT_DISABLED,
  COLOR_TEXT_HINT,
  COLOR_TEXT_PLACEHOLDER,
  COLOR_TEXT_PRIMARY,
  COLOR_TEXT_SUBTLE,
  COLOR_WHITE,
  DEFAULT_FILTER_CONFIG,
  FILTER_FIELDS,
  FILTER_FIELD_LABELS,
  FilterConfig,
  FilterField,
  SUMMARY_REPORT_HEADER_COLUMNS,
  MESES_LIST,
  PAGE_SIZE,
  QUINCENA_VALUE,
  Quincena,
} from "./constants"
import { FiltrosSup } from "./CustomFilters"
import { totalCellStyle, useDashboardStyles } from "./styles"

const SummaryReport = () => {
  const classes = useDashboardStyles()

  const mesActualIndex: number = new Date().getMonth()
  const containerRef = React.useRef<HTMLDivElement>(null)

  // --- ESTADOS DE UI ---
  const [anchorElCol, setAnchorElCol] = React.useState<HTMLElement | null>(null)
  const [anchorElFilter, setAnchorElFilter] =
    React.useState<HTMLElement | null>(null)
  const [openExportDialog, setOpenExportDialog] = React.useState<boolean>(false)
  const [dragIndex, setDragIndex] = React.useState<number | null>(null)

  // --- ESTADOS DE COLUMNAS ---
  const [orderedAccounts, setOrderedAccounts] = React.useState<string[]>([])
  const [visibleAccounts, setVisibleAccounts] = React.useState<Set<string>>(
    new Set(),
  )
  const [groupedByAccount, setGroupedByAccount] = React.useState<boolean>(true)
  const [loadingGrouped, setLoadingGrouped] = React.useState<boolean>(false)

  // --- ESTADOS DE DATA ---
  const [invoicesSummary, setInvoicesSummary] = React.useState<
    IInvoiceSummary[]
  >([])
  const [totals, setTotals] = React.useState<AccountTotal[]>([])
  const [totalBaseImponibleAr, setTotalBaseImponibleAr] = React.useState<number | null>(null)
  const [totalIvaAr, setTotalIvaAr] = React.useState<number | null>(null)
  const [page, setPage] = React.useState<number>(0)
  const [loading, setLoading] = React.useState<boolean>(false)
  const [hasMore, setHasMore] = React.useState<boolean>(true)

  // --- ESTADOS DE FILTROS ---
  const [filtrosSup, setFiltrosSup] = React.useState<FiltrosSup>({
    anio: String(new Date().getFullYear()),
    mes: MESES_LIST[mesActualIndex],
    mesNumero: mesActualIndex + 1,
    quincena: Quincena.Q1,
  })

  const [filterConfig, setFilterConfig] = React.useState<FilterConfig>(
    DEFAULT_FILTER_CONFIG,
  )

  const loadVentas = useCallback(
    async (
      currentPage: number,
      isNewSearch: boolean = false,
      overrideFilters?: any,
      overrideGrouped?: boolean,
    ) => {
      if (loading || (!hasMore && !isNewSearch)) return

      setLoading(true)
      if (overrideGrouped !== undefined) setLoadingGrouped(true)
      try {
        const targetFilters = overrideFilters || filterConfig
        const params = {
          page: currentPage,
          size: PAGE_SIZE,
          year: filtrosSup.anio,
          month: filtrosSup.mesNumero,
          quincena: QUINCENA_VALUE[filtrosSup.quincena as Quincena],
          invoiceNumber: targetFilters.noDoc.join(","),
          rif: targetFilters.rif.join(","),
          clientName: targetFilters.clientName.join(","),
          grouped:
            overrideGrouped !== undefined ? overrideGrouped : groupedByAccount,
        }

        const data: ILedgerSummaryResponse =
          await getLedgerSummaryService(params)

        if (isNewSearch) {
          setOrderedAccounts(data.accounts || [])
          setVisibleAccounts(new Set(data.accounts || []))
          setTotals(data.totals || [])
          setTotalBaseImponibleAr(data.totalBaseImponibleAr ?? null)
          setTotalIvaAr(data.totalIvaAr ?? null)
        }

        const nuevas = data.invoices.content
        setInvoicesSummary((prev) =>
          isNewSearch ? nuevas : [...prev, ...nuevas],
        )
        setHasMore(!data.invoices.last)
        setPage(currentPage + 1)
      } catch (error) {
        console.error("Error cargando data:", error)
      } finally {
        setLoading(false)
        setLoadingGrouped(false)
      }
    },
    [filtrosSup, filterConfig, loading, hasMore, groupedByAccount],
  )

  React.useEffect(() => {
    loadVentas(0, true)
  }, [filtrosSup.anio, filtrosSup.mesNumero, filtrosSup.quincena])

  const handleExportSummary = async (emails: string[]) => {
    await exportSummaryReportService(
      {
        year: filtrosSup.anio,
        month: filtrosSup.mesNumero,
        quincena: QUINCENA_VALUE[filtrosSup.quincena as Quincena],
        invoiceNumber: filterConfig.noDoc.join(",") || undefined,
        rif: filterConfig.rif.join(",") || undefined,
        clientName: filterConfig.clientName.join(",") || undefined,
        grouped: groupedByAccount,
      },
      emails,
    )
  }

  const handleScroll = () => {
    const el = containerRef.current
    if (!el) return
    const { scrollTop, scrollHeight, clientHeight } = el
    if (scrollHeight - scrollTop <= clientHeight + 100 && !loading && hasMore) {
      loadVentas(page)
    }
  }

  return (
    <Box>
      <Box
        style={{
          width: "100%",
          padding: "5px 20px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h5"
          style={{
            color: COLOR_PRIMARY,
            fontWeight: 900,
            textTransform: "uppercase",
            marginTop: 0.2,
          }}
        >
          RESUMEN MAYOR
        </Typography>
      </Box>

      <CustomFilters filtrosSup={filtrosSup} setFiltrosSup={setFiltrosSup} />

      <Box className={classes.toolbar}>
        <Button
          size="small"
          variant="contained"
          startIcon={<Icon style={{ fontSize: 18, color: COLOR_WHITE }}>download</Icon>}
          onClick={() => setOpenExportDialog(true)}
          style={{
            backgroundColor: COLOR_PRIMARY,
            color: COLOR_WHITE,
            fontWeight: 600,
            borderRadius: 20,
            textTransform: "none",
            fontSize: "0.72rem",
            padding: "4px 14px",
            boxShadow: "0px 3px 10px rgba(212,5,17,0.4)",
          }}
        >
          Descargar Reporte
        </Button>

        {/* Toggle Agrupado por Cuenta */}
        <Box
          onClick={() => {
            if (loadingGrouped) return
            const next = !groupedByAccount
            setGroupedByAccount(next)
            loadVentas(0, true, undefined, next)
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 12px",
            borderRadius: 20,
            cursor: loadingGrouped ? "not-allowed" : "pointer",
            backgroundColor: groupedByAccount ? COLOR_PRIMARY : "transparent",
            border: `1px solid ${groupedByAccount ? COLOR_PRIMARY : COLOR_TEXT_BORDER}`,
            transition: "all 0.2s",
            userSelect: "none",
            opacity: loadingGrouped ? 0.7 : 1,
          }}
        >
          {loadingGrouped ? (
            <CircularProgress
              size={14}
              style={{ color: groupedByAccount ? COLOR_WHITE : COLOR_TEXT_HINT }}
            />
          ) : (
            <Icon
              style={{
                fontSize: 17,
                color: groupedByAccount ? COLOR_WHITE : COLOR_TEXT_HINT,
                transition: "color 0.2s",
              }}
            >
              {groupedByAccount ? "account_tree" : "view_list"}
            </Icon>
          )}
          <Typography
            variant="caption"
            style={{
              fontSize: "0.72rem",
              fontWeight: groupedByAccount ? 700 : 400,
              color: groupedByAccount ? COLOR_WHITE : COLOR_TEXT_HINT,
              transition: "all 0.2s",
              whiteSpace: "nowrap",
            }}
          >
            {groupedByAccount ? "Agrupado por cuenta" : "Agrupar por cuenta"}
          </Typography>
          <Box
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              backgroundColor: groupedByAccount ? "#4caf50" : COLOR_TEXT_BORDER,
              flexShrink: 0,
              transition: "background-color 0.2s",
            }}
          />
        </Box>

        <IconButton
          onClick={(e) => setAnchorElFilter(e.currentTarget)}
          style={{ padding: 8, margin: 0 }}
        >
          <Icon style={{ fontSize: 21 }}>filter_list</Icon>
        </IconButton>
        <IconButton
          onClick={(e) => setAnchorElCol(e.currentTarget)}
          style={{ padding: 8, margin: 0 }}
        >
          <Icon style={{ fontSize: 20 }}>view_column</Icon>
        </IconButton>
      </Box>

      {/* POPOVER: COLUMNAS */}
      <Popover
        open={Boolean(anchorElCol)}
        anchorEl={anchorElCol}
        onClose={() => setAnchorElCol(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          style: {
            borderRadius: "16px",
            boxShadow: "0px 12px 40px rgba(0,0,0,0.18)",
            width: "320px",
            border: `1px solid ${COLOR_BORDER_LIGHT}`,
          },
        }}
      >
        <Box className={classes.popoverHeader}>
          <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Icon style={{ fontSize: 20, color: "rgba(255,255,255,0.9)" }}>
              view_column
            </Icon>
            <Typography
              variant="subtitle1"
              className={classes.popoverHeaderTitle}
            >
              Columnas visibles
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setAnchorElCol(null)}>
            <Icon style={{ fontSize: 20, color: "rgba(255,255,255,0.85)" }}>
              close
            </Icon>
          </IconButton>
        </Box>

        <Box className={classes.popoverActions}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Icon style={{ fontSize: 15 }}>done_all</Icon>}
            style={{
              fontSize: "0.75rem",
              padding: "3px 10px",
              borderColor: COLOR_PRIMARY,
              color: COLOR_PRIMARY,
              borderRadius: 8,
              textTransform: "none",
              flex: 1,
            }}
            onClick={() => setVisibleAccounts(new Set(orderedAccounts))}
          >
            Todas
          </Button>
          <Button
            size="small"
            variant="outlined"
            startIcon={<Icon style={{ fontSize: 15 }}>remove_done</Icon>}
            style={{
              fontSize: "0.75rem",
              padding: "3px 10px",
              borderColor: COLOR_TEXT_PLACEHOLDER,
              color: COLOR_TEXT_SUBTLE,
              borderRadius: 8,
              textTransform: "none",
              flex: 1,
            }}
            onClick={() => setVisibleAccounts(new Set())}
          >
            Ninguna
          </Button>
        </Box>

        <Box
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 18px 4px",
            backgroundColor: COLOR_SURFACE_LIGHT,
          }}
        >
          <Icon style={{ fontSize: 13, color: COLOR_TEXT_DISABLED }}>info_outline</Icon>
          <Typography
            variant="caption"
            style={{ color: COLOR_TEXT_DISABLED, fontSize: "0.7rem" }}
          >
            Arrastra para reordenar las columnas
          </Typography>
        </Box>

        <Divider style={{ backgroundColor: COLOR_BORDER_LIGHT }} />

        <Box
          style={{ maxHeight: 340, overflowY: "auto", padding: "6px 0 10px" }}
        >
          {orderedAccounts.map((acc, index) => (
            <Box
              key={acc}
              draggable
              onDragStart={() => setDragIndex(index)}
              onDragOver={(e) => {
                e.preventDefault()
                if (dragIndex === null || dragIndex === index) return
                setOrderedAccounts((prev) => {
                  const next = [...prev]
                  const [moved] = next.splice(dragIndex, 1)
                  next.splice(index, 0, moved)
                  return next
                })
                setDragIndex(index)
              }}
              onDragEnd={() => setDragIndex(null)}
              className={classes.draggableRow}
              style={{
                backgroundColor:
                  dragIndex === index ? COLOR_CHIP_BG : "transparent",
                borderLeft:
                  dragIndex === index
                    ? `3px solid ${COLOR_PRIMARY}`
                    : "3px solid transparent",
              }}
            >
              <Icon
                style={{
                  fontSize: 22,
                  color: dragIndex === index ? COLOR_PRIMARY : COLOR_TEXT_BORDER,
                  transition: "color 0.15s",
                  flexShrink: 0,
                }}
              >
                drag_indicator
              </Icon>
              <input
                type="checkbox"
                checked={visibleAccounts.has(acc)}
                onChange={(e) => {
                  setVisibleAccounts((prev) => {
                    const next = new Set(prev)
                    if (e.target.checked) next.add(acc)
                    else next.delete(acc)
                    return next
                  })
                }}
                style={
                  {
                    accentColor: COLOR_PRIMARY,
                    width: 16,
                    height: 16,
                    cursor: "pointer",
                    flexShrink: 0,
                  } as React.CSSProperties
                }
              />
              <Typography
                variant="body2"
                style={{
                  fontSize: "0.88rem",
                  fontWeight: visibleAccounts.has(acc) ? 600 : 400,
                  color: visibleAccounts.has(acc) ? COLOR_TEXT_PRIMARY : COLOR_TEXT_PLACEHOLDER,
                  flex: 1,
                  transition: "all 0.15s",
                  letterSpacing: 0.2,
                }}
              >
                {acc}
              </Typography>
              {visibleAccounts.has(acc) && (
                <Icon style={{ fontSize: 16, color: COLOR_PRIMARY }}>
                  visibility
                </Icon>
              )}
            </Box>
          ))}
          {orderedAccounts.length === 0 && (
            <Box
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                padding: "24px 16px",
                gap: 8,
              }}
            >
              <Icon style={{ fontSize: 32, color: COLOR_ICON_EMPTY }}>table_chart</Icon>
              <Typography
                variant="caption"
                style={{ color: COLOR_TEXT_PLACEHOLDER, textAlign: "center" }}
              >
                No hay cuentas cargadas
              </Typography>
            </Box>
          )}
        </Box>

        <Box className={classes.popoverFooter}>
          <Typography
            variant="caption"
            style={{ color: COLOR_TEXT_HINT, fontSize: "0.75rem" }}
          >
            {visibleAccounts.size} de {orderedAccounts.length} columnas visibles
          </Typography>
          <Icon style={{ fontSize: 15, color: COLOR_TEXT_BORDER }}>drag_handle</Icon>
        </Box>
      </Popover>

      {/* POPOVER: FILTROS COMBINADOS */}
      <Popover
        open={Boolean(anchorElFilter)}
        anchorEl={anchorElFilter}
        onClose={() => setAnchorElFilter(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          style: {
            borderRadius: "16px",
            width: "550px",
            boxShadow: "0px 12px 40px rgba(0,0,0,0.18)",
            border: `1px solid ${COLOR_BORDER_LIGHT}`,
          },
        }}
      >
        <Box className={classes.popoverHeader}>
          <Box style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Icon style={{ fontSize: 20, color: "rgba(255,255,255,0.9)" }}>
              filter_list
            </Icon>
            <Typography
              variant="subtitle1"
              className={classes.popoverHeaderTitle}
            >
              Filtros Combinados
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setAnchorElFilter(null)}>
            <Icon style={{ fontSize: 20, color: "rgba(255,255,255,0.85)" }}>
              close
            </Icon>
          </IconButton>
        </Box>

        <Box style={{ padding: "16px 20px 20px" }}>
          <Box
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "32px",
              marginBottom: 14,
            }}
          >
            <FormControl
              variant="standard"
              style={{ width: "180px", flexShrink: 0 }}
            >
              <InputLabel style={{ fontSize: "0.85rem" }}>Campo</InputLabel>
              <Select
                value={filterConfig.activeField}
                onChange={(e) =>
                  setFilterConfig({
                    ...filterConfig,
                    activeField: e.target.value as FilterField,
                  })
                }
              >
                {FILTER_FIELDS.map(({ field, label }) => (
                  <MenuItem key={field} value={field}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              variant="standard"
              label={`Agregar ${FILTER_FIELD_LABELS[filterConfig.activeField]} y presione Enter`}
              fullWidth
              value={filterConfig.inputValue}
              onChange={(e) =>
                setFilterConfig({ ...filterConfig, inputValue: e.target.value })
              }
              onKeyDown={(e) => {
                if (e.key === "Enter" && filterConfig.inputValue.trim()) {
                  const field = filterConfig.activeField
                  if (
                    !filterConfig[field].includes(
                      filterConfig.inputValue.trim(),
                    )
                  ) {
                    setFilterConfig({
                      ...filterConfig,
                      [field]: [
                        ...filterConfig[field],
                        filterConfig.inputValue.trim(),
                      ],
                      inputValue: "",
                    })
                  } else {
                    setFilterConfig({ ...filterConfig, inputValue: "" })
                  }
                }
              }}
            />
          </Box>

          <Divider style={{ backgroundColor: COLOR_BORDER_LIGHT, marginBottom: 12 }} />

          <Box className={classes.filterChipsContainer}>
            {FILTER_FIELDS.map(({ field, label }) => {
              const chips = filterConfig[field as FilterField] as string[]
              if (chips.length === 0) return null
              return (
                <Box key={field} style={{ marginBottom: 6 }}>
                  <Box className={classes.filterCategoryChip}>
                    <Typography
                      variant="caption"
                      style={{
                        fontSize: "0.68rem",
                        fontWeight: 700,
                        color: COLOR_WHITE,
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      {label}
                    </Typography>
                  </Box>
                  <Box style={{ display: "flex", flexWrap: "wrap" }}>
                    {chips.map((val, i) => (
                      <Chip
                        key={i}
                        label={val}
                        size="small"
                        style={{
                          margin: 3,
                          backgroundColor: COLOR_CHIP_BG,
                          color: COLOR_PRIMARY_DARK,
                          fontWeight: 600,
                          fontSize: "0.75rem",
                          borderRadius: 6,
                        }}
                        onDelete={() => {
                          const updated = chips.filter((_, idx) => idx !== i)
                          setFilterConfig({ ...filterConfig, [field]: updated })
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )
            })}

            {filterConfig.noDoc.length === 0 &&
              filterConfig.rif.length === 0 &&
              filterConfig.clientName.length === 0 && (
                <Box
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    height: 80,
                    gap: 6,
                  }}
                >
                  <Icon style={{ fontSize: 28, color: COLOR_ICON_EMPTY }}>
                    filter_list_off
                  </Icon>
                  <Typography variant="caption" style={{ color: COLOR_TEXT_PLACEHOLDER }}>
                    No hay filtros activos
                  </Typography>
                </Box>
              )}
          </Box>
        </Box>

        <Box
          style={{
            borderTop: `1px solid ${COLOR_BORDER_LIGHT}`,
            padding: "10px 20px 14px",
            backgroundColor: COLOR_SURFACE_LIGHT,
            borderRadius: "0 0 16px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            variant="outlined"
            size="small"
            startIcon={<Icon style={{ fontSize: 16 }}>delete_sweep</Icon>}
            style={{
              color: COLOR_PRIMARY_DARK,
              borderColor: COLOR_PRIMARY_DARK,
              borderRadius: 8,
              textTransform: "none",
              fontSize: "0.8rem",
            }}
            onClick={() => {
              setFilterConfig(DEFAULT_FILTER_CONFIG)
              loadVentas(0, true, DEFAULT_FILTER_CONFIG)
              setAnchorElFilter(null)
            }}
          >
            Limpiar Todo
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<Icon style={{ fontSize: 16 }}>check</Icon>}
            style={{
              backgroundColor: COLOR_PRIMARY,
              color: COLOR_WHITE,
              borderRadius: 8,
              textTransform: "none",
              fontSize: "0.8rem",
              boxShadow: "0px 4px 12px rgba(212,5,17,0.35)",
            }}
            onClick={() => {
              loadVentas(0, true)
              setAnchorElFilter(null)
            }}
          >
            Aplicar Filtros
          </Button>
        </Box>
      </Popover>

      {/* TABLA PRINCIPAL */}
      <TableContainer
        ref={containerRef}
        component={Paper}
        onScroll={handleScroll}
        style={{ maxHeight: "calc(100vh - 220px)", overflow: "auto" }}
      >
        <Table
          stickyHeader
          size="small"
          style={{
            tableLayout: "fixed",
            width: "fit-content",
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <TableHead>
            <TableRow>
              {SUMMARY_REPORT_HEADER_COLUMNS.map((col) => (
                <TableCell
                  key={col.label}
                  align="center"
                  className={classes.headerCell}
                  style={{ width: col.width }}
                >
                  {col.label}
                </TableCell>
              ))}
              {orderedAccounts
                .filter((acc) => visibleAccounts.has(acc))
                .map((acc) => (
                  <TableCell
                    key={acc}
                    align="center"
                    className={classes.headerCell}
                    style={{ width: 140 }}
                  >
                    {acc}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {invoicesSummary.map((row, index) => (
              <TableRow key={`${row.invoiceNumber}-${index}`} hover>
                <TableCell
                  align="center"
                  className={classes.fixedCell}
                  style={{ width: 120 }}
                >
                  {row.invoiceNumber}
                </TableCell>
                <TableCell
                  align="right"
                  className={classes.dataCell}
                  style={{ width: 120 }}
                >
                  {row.rif}
                </TableCell>
                <TableCell
                  align="left"
                  className={classes.dataCell}
                  style={{ width: 300, whiteSpace: "normal" }}
                >
                  {row.clientName}
                </TableCell>
                <TableCell align="right" className={classes.dataCell} style={{ width: 160 }}>
                  {row.baseImponibleAr != null
                    ? row.baseImponibleAr.toLocaleString("es-VE", { minimumFractionDigits: 2 })
                    : ""}
                </TableCell>
                <TableCell align="right" className={classes.dataCell} style={{ width: 160 }}>
                  {row.ivaAr != null
                    ? row.ivaAr.toLocaleString("es-VE", { minimumFractionDigits: 2 })
                    : ""}
                </TableCell>
                {orderedAccounts
                  .filter((acc) => visibleAccounts.has(acc))
                  .map((accCode) => {
                    const accountData = row.accountValues.find(
                      (av) => av.accountNumber === accCode,
                    )
                    return (
                      <TableCell
                        key={accCode}
                        align="right"
                        className={classes.dataCell}
                        style={{ width: 140 }}
                      >
                        {accountData
                          ? accountData.amount.toLocaleString("es-VE", {
                              minimumFractionDigits: 2,
                            })
                          : ""}
                      </TableCell>
                    )
                  })}
              </TableRow>
            ))}
          </TableBody>

          <TableFooter>
            <TableRow>
              <TableCell
                align="center"
                style={{ ...totalCellStyle, width: 120 }}
              >
                Total
              </TableCell>
              <TableCell style={{ ...totalCellStyle, width: 120 }} />
              <TableCell style={{ ...totalCellStyle, width: 300 }} />
              <TableCell align="right" style={{ ...totalCellStyle, width: 160 }}>
                {totalBaseImponibleAr != null
                  ? totalBaseImponibleAr.toLocaleString("es-VE", { minimumFractionDigits: 2 })
                  : ""}
              </TableCell>
              <TableCell align="right" style={{ ...totalCellStyle, width: 160 }}>
                {totalIvaAr != null
                  ? totalIvaAr.toLocaleString("es-VE", { minimumFractionDigits: 2 })
                  : ""}
              </TableCell>
              {orderedAccounts
                .filter((acc) => visibleAccounts.has(acc))
                .map((acc) => {
                  const total = totals.find((t) => t.accountNumber === acc)
                  return (
                    <TableCell
                      key={acc}
                      align="right"
                      style={{ ...totalCellStyle, width: 140 }}
                    >
                      {total
                        ? total.amount.toLocaleString("es-VE", {
                            minimumFractionDigits: 2,
                          })
                        : ""}
                    </TableCell>
                  )
                })}
            </TableRow>
          </TableFooter>
        </Table>

        {loading && (
          <Box
            style={{ display: "flex", justifyContent: "center", padding: 16 }}
          >
            <CircularProgress size={30} />
          </Box>
        )}
        {!loading && invoicesSummary.length === 0 && (
          <Box className={classes.emptyState}>
            <Icon style={{ fontSize: 40, color: COLOR_ICON_EMPTY }}>search_off</Icon>
            <Typography
              variant="body2"
              style={{ color: COLOR_TEXT_DISABLED, fontWeight: 500 }}
            >
              No se encontraron registros para los filtros aplicados
            </Typography>
          </Box>
        )}
      </TableContainer>

      <ExportReportDialog
        open={openExportDialog}
        onClose={() => setOpenExportDialog(false)}
        onConfirm={handleExportSummary}
        title="Descargar Reporte de Resumen Mayor"
        description="El reporte se generará con los filtros activos y será enviado a los correos indicados."
      />
    </Box>
  )
}

export default SummaryReport
